import { ACTION_CORE_API_SUB_DUMP, ACTION_CORE_API_SUB_UPDATE, AppAction } from '@om-fe/store';
import {
    filterAndPick,
    FilterAndPickReturnType,
    filterItemsFromRecordsBySport,
} from '../../utils/filter-items-from-records';
import {
    FavoriteMatchListActionsTypes,
    FavoriteMatchListDisciplineItemStoreModel,
    FavoriteMatchListSetFilterDataAction,
    FavoriteMatchListSetLoadingData,
    FavoriteMatchListStoreModel,
    FavoritesAggregatorOneOfTypeModel,
} from './favorite-match-list.constants';
import {
    createEmptyJournal,
    filterFavoriteMatchListPayload,
    findDeletedElement,
    findDisciplineId,
    getFavoriteMatchListSliceName,
    getFavoriteSportIdInMatchs,
    getMappedSet,
} from './favorite-match-list.utils';
import { FilteredMainMarkets, FilterItemsFromRecordsReturn, MatchListStoreModel } from '../../utils/match-list-models';
import applyUpdatesForMatchList from '../../utils/apply-updates-for-match-list';
import omit from 'lodash/omit';
import {
    AggregatorDisciplineModel,
    AggregatorMainMarketModel,
    AggregatorOneOfTypeModel,
    DisciplineLiveStatus,
    ModelTypes,
} from '@om-fe/models';
import {
    RecordCreate,
    RecordDelete,
    RecordUpdate,
    TopicMessageType,
    UpdateAction,
    UpdateActionJournal,
    UpdateActionPayload,
} from '@om-fe/api';
import { isBrowser } from '@om-fe/utils';

const initialState: FavoriteMatchListStoreModel = {
    ready: false,
    matchId: '',
    userID: 0,
    liveStatus: DisciplineLiveStatus.LIVE,
    isLoadingMoreData: false,
    isLoadingData: true,
    filteredSportIds: [],
    disciplines: {},
    mappedDisciplineSet: undefined,
};

export const favoriteMatchListReducerCreator = (instanceId: string) => (
    state: FavoriteMatchListStoreModel = initialState,
    action: AppAction,
): FavoriteMatchListStoreModel => {
    const sliceName: string = getFavoriteMatchListSliceName(instanceId);

    const { meta } = action;

    if (!meta || meta.slice !== sliceName) {
        return state;
    }
    
    switch (action.type) {

        case FavoriteMatchListActionsTypes.SetLoadingData: {
            const { payload: { isLoadingMoreData } } = action as FavoriteMatchListSetLoadingData;

            return {
                ...state,
                isLoadingData: !isLoadingMoreData,
                isLoadingMoreData,
            };
        }
        case FavoriteMatchListActionsTypes.SetFilterData: {
            const {
                payload: {
                    matchId,
                    mainMarketsLimit,
                },
            } = action as FavoriteMatchListSetFilterDataAction;

            return {
                ...state,
                matchId,
                mainMarketsLimit,
            };
        }

        case ACTION_CORE_API_SUB_DUMP: {
            const { payload } = action as UpdateAction<FavoritesAggregatorOneOfTypeModel>;
            const newPayload: UpdateActionPayload<AggregatorOneOfTypeModel> = filterFavoriteMatchListPayload(payload);
            const records: AggregatorOneOfTypeModel[] = newPayload.records;
            const sportsList: string[] = getFavoriteSportIdInMatchs(records);
            const mutableItems: Record<string, FavoriteMatchListDisciplineItemStoreModel> = {};
            const mappedSet: Record<ModelTypes, Record<string, AggregatorOneOfTypeModel>> = getMappedSet(newPayload);

            sportsList.forEach((sportId: string) => {
                const filteredItemsBySport: FilterItemsFromRecordsReturn = filterItemsFromRecordsBySport(records, sportId);

                mutableItems[sportId] = {
                    ...filteredItemsBySport,
                    isLoadingData: false,
                    isLoadingMoreData: false,
                    fetchedFromBrowser: isBrowser(),
                };
            });

            return {
                ...state,
                filteredSportIds: sportsList,
                ready: true,
                isLoadingData: false,
                mappedDisciplineSet: mappedSet,
                disciplines: mutableItems,
            };
        }
        case ACTION_CORE_API_SUB_UPDATE: {
            const { payload } = action as UpdateAction<FavoritesAggregatorOneOfTypeModel>;
            const newPayload: UpdateActionPayload<AggregatorOneOfTypeModel> = filterFavoriteMatchListPayload(payload);
            const { journal: { creates, updates, deletes }, path, version, records } = newPayload;

            let stateDisciplines: Record<string, FavoriteMatchListDisciplineItemStoreModel> = state.disciplines;
            let stateFilteredSportIds: string[] = state.filteredSportIds;

            const mappedSet: Record<ModelTypes, Record<string, AggregatorOneOfTypeModel>> = getMappedSet(newPayload);
            const oldMappedSet: Record<ModelTypes, Record<string, AggregatorOneOfTypeModel>> = state.mappedDisciplineSet!;
            const mutableUpdatedDisciplineModelJournals: Record<string, UpdateActionJournal<AggregatorOneOfTypeModel>> = {};

            creates.forEach((createdElement: RecordCreate<AggregatorOneOfTypeModel>) => {
                const disciplineId: string | null = findDisciplineId(createdElement, mappedSet);
                
                if (!disciplineId) {
                    return;
                }
                if (!stateFilteredSportIds.includes(disciplineId)) {
                    stateFilteredSportIds = [...stateFilteredSportIds, disciplineId];
                    let filteredMainMarkets: Record<string, FilteredMainMarkets> = {};
                    let filteredMainMarketsList: string[] = [];
                    const hasMainMarket: boolean = !!creates
                        .find((item: RecordCreate<AggregatorOneOfTypeModel>): boolean => {
                            if (item.entityType === ModelTypes.MAIN_MARKET) {
                                return (item as RecordCreate<AggregatorMainMarketModel>).entity.sportId === disciplineId;
                            }

                            return false;
                        });

                    if (!hasMainMarket) {
                        filteredMainMarkets = filterAndPick({
                            filterPredicate: ['_type', ModelTypes.MAIN_MARKET],
                            filterBySportId: disciplineId,
                            data: records,
                        }) as Record<string, FilteredMainMarkets>;

                        filteredMainMarketsList = filterAndPick({
                            filterPredicate: ['_type', ModelTypes.MAIN_MARKET],
                            filterBySportId: disciplineId,
                            data: records,
                            returnType: FilterAndPickReturnType.ArrayOfIds,
                        }) as string[];
                    }

                    stateDisciplines = {
                        ...stateDisciplines,
                        [disciplineId]: {
                            discipline: (mappedSet.SPORT[disciplineId] as AggregatorDisciplineModel),
                            filteredEventInfos: {},
                            filteredEventStatistics: [],
                            filteredMainMarketsList,
                            filteredMainMarkets,
                            filteredMarketGroupsOverviewList: [],
                            filteredMarketGroupsOverview: {},
                            filteredMarketAndOutcomeRelation: {},
                            filteredOutcomes: {},
                            filteredBettingOffers: {},
                            filteredMatchesWithEvents: {},
                            filteredMatchesIds: [],
                            marketsWithOutcomesAndBettingOffers: {},
                            filteredStreamingProviderEventRelation: {},
                            isLoadingData: false,
                            isLoadingMoreData: false,
                            fetchedFromBrowser: isBrowser(),
                        },
                    };
                }
                if (!mutableUpdatedDisciplineModelJournals[disciplineId]) {
                    mutableUpdatedDisciplineModelJournals[disciplineId] = createEmptyJournal();
                }
                mutableUpdatedDisciplineModelJournals[disciplineId].creates.push(createdElement);
            });
            deletes.forEach((deletedElement: RecordDelete) => {
                if (findDeletedElement(deletedElement, oldMappedSet)) {
                    const disciplineId: string | null = findDisciplineId(deletedElement, oldMappedSet);
                    if (!disciplineId) {
                        return;
                    }
                    if (!mutableUpdatedDisciplineModelJournals[disciplineId]) {
                        mutableUpdatedDisciplineModelJournals[disciplineId] = createEmptyJournal();
                    }
                    mutableUpdatedDisciplineModelJournals[disciplineId].deletes.push(deletedElement);
                }

            });
            updates.forEach((updatedElement: RecordUpdate<AggregatorOneOfTypeModel>) => {
                const disciplineId: string | null = findDisciplineId(updatedElement, mappedSet);
                if (!disciplineId) {
                    return;
                }
                if (!mutableUpdatedDisciplineModelJournals[disciplineId]) {
                    mutableUpdatedDisciplineModelJournals[disciplineId] = createEmptyJournal();
                }
                mutableUpdatedDisciplineModelJournals[disciplineId].updates.push(updatedElement);
            });

            Object.keys(mutableUpdatedDisciplineModelJournals).forEach((disciplineId: string) => {
                const {
                    updates: updatesJournal,
                    deletes: deletesJournal,
                    creates: createsJournal,
                } = mutableUpdatedDisciplineModelJournals[disciplineId];
                const updatePayload: UpdateActionPayload<AggregatorOneOfTypeModel> = {
                    journal: mutableUpdatedDisciplineModelJournals[disciplineId],
                    records: [...newPayload.records],
                    summary: {
                        messageType: TopicMessageType.UPDATE,
                        created: createsJournal.length,
                        deleted: deletesJournal.length,
                        updated: updatesJournal.length,
                    },
                    path,
                    version,
                };
                
                const updatedModel: MatchListStoreModel = applyUpdatesForMatchList(updatePayload, stateDisciplines[disciplineId]);
                if (updatedModel.filteredMatchesIds.length > 0) {
                    stateDisciplines = {
                        ...stateDisciplines,
                        [disciplineId]: {
                            ...updatedModel,
                            isLoadingMoreData: false,
                            isLoadingData: false,
                        },
                    };
                } else {
                    stateDisciplines = omit(stateDisciplines, disciplineId);
                    stateFilteredSportIds = stateFilteredSportIds.filter((iterableDisciplineId: string) => iterableDisciplineId !== disciplineId);
                }
            });

            return {
                ...state,
                disciplines: stateDisciplines,
                filteredSportIds: stateFilteredSportIds,
                mappedDisciplineSet: mappedSet,
                isLoadingMoreData: false,
            };
        }
        default:
            return state;
    }
};
